<script setup lang="ts">
/**
 * @author Beka Chkhaidze
 */
import { ref, watch, onBeforeMount, computed, defineAsyncComponent } from "vue";
import { useCalendar, formatter } from "@/components/regularComponents/FormComponents/CustomCalendar/ts/calendar";
import { useBookingStore } from "@/store/booking";
import { reqAxios } from "@/composables/core/func";
import type { BaseTaxonomy } from "@/store/main/types/widgets-terms/terms";

import { useMainStore } from "@/store/main/index";

import TimesList from "./TimesList.vue";
import Calendar from "@/components/regularComponents/FormComponents/CustomCalendar/Calendar.vue";

const VideoCallModal = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/Modals/VideoCallModal.vue")
);
const BaseModal = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/Modals/BaseModal.vue")
);

const ResponseModal = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/Modals/ResponseModal.vue")
);

const mainStore = useMainStore();
const bookingStore = useBookingStore();
const { getBookedHours, bookedDays } = useCalendar();

const showStep2 = ref(false);
const dateChanged = ref(false);
const bookedHours = ref<string[]>();
const isRequestFinished = ref(false);
const isSuccessful = ref(false);
const isModalOpen = ref(false);
const newHeight = ref<number>();

const isMob = computed(() => mainStore.windowWidth <= mainStore.breakpoints.mobile);

watch(
  () => bookingStore.date,
  (n) => {
    dateChanged.value = true;
    bookedHours.value = getBookedHours(formatter(new Date(n)));
  }
);

watch(bookedDays, () => {
  bookedHours.value = getBookedHours();
});

const onSelect = (item: BaseTaxonomy) => {
  showStep2.value = true;
  bookingStore.time = item?.title;
  // toggleScroll(false);
};

const onBack = () => {
  showStep2.value = false;
  // bookingStore.$reset();
};

const onHeightChange = (_newHeight: number) => {
  newHeight.value = _newHeight;
};

const sendReq = async () => {
  const params = {
    date: bookingStore.getFormatForForm,
    time: bookingStore.time,
    dial: bookingStore.dial,
    timezone: bookingStore.timezone?.title,
    email: bookingStore.email,
    name: bookingStore.name,
  };

  bookingStore.$reset();

  const response = await reqAxios("main/saveSubmitedForm", {
    formType: "bookedCalls",
    ...params,
  });

  isSuccessful.value = response?.data?.[0] === "success";
  isRequestFinished.value = true;
  dateChanged.value = false;
  isModalOpen.value = true;
  showStep2.value = false;
};

onBeforeMount(async () => {
  bookedHours.value = getBookedHours();
});
</script>

<template>
  <div class="contact-us__meeting">
    <div class="contact-us__message-title uppercase title">
      <p>{{ $t("want video call wit us? choose meeting time.") }}</p>
    </div>
    <ClientOnly>
    <div
      :class="[
        'contact-us__meeting-booking d-flex',
        { 'contact-us__meeting-booking--changed': isMob && dateChanged },
      ]"
      :style="`--max-h: ${newHeight}px;`"
    >
      <VideoCallModal
        :showBack="true"
        v-if="showStep2"
        @back="onBack"
        @clicked="sendReq"
      />
      <template v-else>
        <div class="date">
          <Calendar
            direction="top"
            v-model="bookingStore.date"
            v-model:timezone="bookingStore.timezone"
            @height-changed="onHeightChange"
          />
        </div>
        <TimesList
          v-bind="{ calendarModal: bookingStore.date, bookedHours, showBack: isMob }"
          @select="onSelect"
          @back="dateChanged = false"
        />
      </template>
    </div>

    <Teleport to="body">
      <Transition name="fade-out">
        <BaseModal v-if="isModalOpen && isRequestFinished" @close="isModalOpen = false">
          <ResponseModal :success="isSuccessful" @close="isModalOpen = false" />
        </BaseModal>
      </Transition>
    </Teleport>
  </ClientOnly>

  </div>
</template>
